import { ZoneId } from "@js-joda/core";
import { type PropsWithChildren, useCallback, useMemo, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
	type GeneratePostsDto,
	getBlogPlatforms,
	getSocialPlatforms,
	Platform,
	type PlatformValue,
} from "shared";
import { useEventCallback } from "usehooks-ts";
import {
	DateRangeSelector,
	type DateRangeValue,
} from "../calendar/DateRangeSelector";
import { usePostsActions } from "../calendar/usePosts";
import { useForm } from "../common/form/useForm";
import { Modal, useModal } from "../common/modal/Modal";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { Button } from "../components/Button";
import { PlatformSelector } from "../components/PlatformSelector";
import { useFeatureFlag } from "../hooks/use-app-metadata";
import { LinkGen } from "../LinkGen";
import { defaultSelectedPlatforms } from "../settings/connected-platforms";
import {
	useConnectedPlatforms,
	useIntegrations,
} from "../settings/useIntegrations";
import "./ModalGeneratePosts.css";
import { z } from "zod/v4";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { withAlertScope } from "../common/alert/Alert";

const GeneratePostsSchema = z.object({
	platform: z
		.array(z.nativeEnum(Platform))
		.min(1, "Please select at least 1 platform"),
	specialInstructions: z.string().optional(),
	exclusions: z.string().optional(),
	from: z.string(),
	to: z.string(),
	zone: z.string(),
});

interface Props extends PropsWithChildren {
	isBlog: boolean;
}

export const ModalGeneratePosts = withAlertScope<Props>(({ isBlog }) => {
	const { close } = useModal();
	const { generate, generateBlog } = usePostsActions();
	const { mutateAsync: generatePosts, isPending } = generate;
	const { mutateAsync: generateBlogPosts, isPending: isBlogPending } =
		generateBlog;
	const { atLeastOneConnected } = useIntegrations();
	// Full per-platform detail is only needed here, inside a modal the user
	// opens deliberately — not on every calendar page load.
	const connectedPlatforms = useConnectedPlatforms();
	const [showInfo, setShowInfo] = useState(false);
	const isYouTubeEnabled = useFeatureFlag("YouTubeIntegration");
	const isTikTokEnabled = useFeatureFlag("TikTokIntegration");
	const isXEnabled = useFeatureFlag("XIntegration");

	const selectablePlatforms = useMemo(() => {
		const all = isBlog ? getBlogPlatforms() : getSocialPlatforms();
		return all.filter((platform) => {
			if (platform === Platform.YOUTUBE && !isYouTubeEnabled) {
				return false;
			}
			if (platform === Platform.TIKTOK && !isTikTokEnabled) {
				return false;
			}
			if (platform === Platform.X && !isXEnabled) {
				return false;
			}
			return true;
		});
	}, [isBlog, isYouTubeEnabled, isTikTokEnabled, isXEnabled]);

	const form = useForm<GeneratePostsDto>({
		initialValues: {
			platform: defaultSelectedPlatforms(
				selectablePlatforms,
				connectedPlatforms,
			),
			specialInstructions: "",
			exclusions: "",
			from: "",
			to: "",
			zone: ZoneId.systemDefault().toString(),
		},
		validationSchema: toFormikValidationSchema(GeneratePostsSchema),
		onSubmit: async (values) => {
			try {
				setShowInfo(true);
				await (isBlog ? generateBlogPosts(values) : generatePosts(values));
			} catch (e) {
				setShowInfo(false);
				throw e;
			}

			close();
		},
	});

	const handleDatesChange = useEventCallback(
		(value: DateRangeValue | undefined) => {
			if (value === undefined) {
				form.setValues(
					(prev) => ({
						...prev,
						from: "",
						to: "",
					}),
					true,
				);
			} else {
				const [from, to] = value;
				form.setValues(
					(prev) => ({
						...prev,
						from: from.toString(),
						to: to.toString(),
					}),
					true,
				);
			}
		},
	);

	const handlePlatformChange = useCallback(
		(value: Platform | PlatformValue) => {
			if (form.values.platform.includes(value)) {
				form.setFieldValue(
					"platform",
					form.values.platform.filter((platform) => platform !== value),
					true,
				);
			} else {
				form.setFieldValue("platform", [...form.values.platform, value], true);
			}
		},
		[form],
	);

	const hasErrorRange = !!form.errors.from || !!form.errors.to;

	return (
		<Modal size="lg">
			{!showInfo && (
				<Form onSubmit={form.handleSubmit}>
					<p>
						Please select the time period for generating content.
						<br />
						You can always revisit this step to make a different choice.
					</p>
					<DateRangeSelector onChange={handleDatesChange} className="mb-3" />
					{hasErrorRange && (
						<p className="form-error">Please select date range</p>
					)}

					<p>Please chose platforms to generate content for:</p>

					<PlatformSelector
						platforms={selectablePlatforms}
						values={new Set(form.values.platform)}
						onChange={handlePlatformChange}
						connected={connectedPlatforms}
					/>

					{form.errors.platform && (
						<p className="form-error">{form.errors.platform}</p>
					)}

					<ExtraForm.Group label="Special instructions">
						<ExtraForm.Control
							placeholder='Put in any special campaigns like "birthday" specials or "special mentions'
							{...form.getFieldProps("specialInstructions")}
							{...form.getFieldState("specialInstructions")}
						/>
					</ExtraForm.Group>
					<ExtraForm.Group label="Exclusions">
						<ExtraForm.Control
							as="textarea"
							rows={3}
							placeholder='Type in any thing that you would not want to include in your content such as "remove any references to these specific brands."'
							{...form.getFieldProps("exclusions")}
							{...form.getFieldState("exclusions")}
						/>
					</ExtraForm.Group>
					{!atLeastOneConnected ? (
						<Alert variant="warning">
							No social networks are currently connected to your profile. Please
							visit our{" "}
							<Link onClick={close} to={LinkGen.Settings.Apps()}>
								Apps section under Settings
							</Link>{" "}
							to connect your social networks.
						</Alert>
					) : null}
					<Button
						loading={isBlog ? isBlogPending : isPending}
						// TODO temporarily disable: disabled={!atLeastOneConnected}
						disabled={form.values.platform.length === 0 || !form.isValid}
						icon="backup"
						className="mt-3"
						full
						type="submit"
					>
						{isBlog ? "Generate Blogs Posts" : "Generate Content"}
					</Button>
				</Form>
			)}
			{showInfo && (
				<div className="info-generate">
					<p>
						<strong>
							We are currently creating all of your{" "}
							{isBlog ? "blogs" : "social media"} posts for the period you
							defined. After this is complete, you will see the posts listed in
							the calendar.
						</strong>
					</p>

					<ol>
						<li>
							<p>
								<strong>You should review all post</strong> content,{" "}
								<strong>upload</strong> any images that fit your needs and{" "}
								<strong>make any changes</strong> that you would like.
							</p>
						</li>
						<li>
							<p>
								<strong>On the calendar</strong>, you will see a{" "}
								<strong>red dot</strong> ( <span className="circle" /> ) next to
								any posts that are <strong>not in compliance</strong> with the
								rules of the platform you are posting to, if these are not
								resolved, our system will not post them as scheduled.
							</p>
						</li>
					</ol>
					<Button
						loading={isBlog ? isBlogPending : isPending}
						full
						onClick={close}
					>
						Ok
					</Button>
				</div>
			)}
		</Modal>
	);
});
